/**
 * g45kit — primitives for गोत्र 45s "LIT ARCHIVE".
 *
 * Written fresh 2026-08-15. ⛔ Deliberately shares NOTHING with kit45.tsx / VerA–VerD,
 * which were rejected wholesale. Only DATA is reused (words45.ts, the PNGs, the audio).
 *
 * The laws this file hard-codes so a beat author cannot break them:
 *   L1  every animated value is quantised to every 2nd frame  → useStep()
 *   L2  nothing fades in; things SWING (damping 8) and ring    → useSwing()
 *   L3  one instance travels between poses                     → useKeys()
 *   L4  exactly one accent, AMBER, on photoreal dark ground     → C.amber
 *   L5  ground is optically pushed back (blur/haze/grade), cast carries a real shadow
 *   L6  type is anchored to the spoken word                     → <Line> / <Big>
 *   L8  no new vector furniture — depth and light do the work
 *
 * The one structural idea: the cast does not sit ON the photograph, it stands INSIDE it.
 * <Scene> splits each plate into 5 inpainted depth slices and renders the cast between
 * slice 2 and slice 3, so the near ground occludes the figures. That is the whole look.
 */
import React from "react";
import {
  AbsoluteFill,
  Img,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
  Easing,
} from "remotion";
import { loadFont as loadAnek } from "@remotion/google-fonts/AnekDevanagari";
import { loadFont as loadTiro } from "@remotion/google-fonts/TiroDevanagariHindi";
import { loadFont as loadInter } from "@remotion/google-fonts/Inter";

/* ─────────────────────────────────────────────────────────────────────────────
   TYPE — a real pairing, not one neutral family used at three sizes.

   Anek Devanagari (Ek Type, 100–800)  = the modern voice. Headlines, labels, the
                                         channel's own speech. Wide weight range is
                                         what lets hierarchy exist without colour.
   Tiro Devanagari Hindi (400 only)    = the shastra voice. Calligraphic, single
                                         weight, used ONLY for words that come from
                                         the tradition itself (जड़, आर्ष, ऋषि).
   Inter                               = Latin/numerals ONLY. ⛔ never Devanagari —
                                         Inter has no Devanagari glyphs and ships □□□.
   ───────────────────────────────────────────────────────────────────────────── */
const { fontFamily: anekBase } = loadAnek("normal", {
  weights: ["300", "400", "500", "600", "700", "800"],
  subsets: ["devanagari", "latin"],
});
const { fontFamily: tiroBase } = loadTiro("normal", {
  weights: ["400"],
  subsets: ["devanagari", "latin"],
});
const { fontFamily: interBase } = loadInter("normal", {
  weights: ["400", "500", "600"],
  subsets: ["latin"],
});

export const anek = `${anekBase}, ${interBase}, sans-serif`;
export const tiro = `${tiroBase}, ${anekBase}, serif`;
export const inter = interBase;

/** ⛔ letterSpacing shears the shirorekha. Devanagari gets wordSpacing only. */
export const devaBlock: React.CSSProperties = {
  fontFamily: anek,
  wordSpacing: "0.06em",
  letterSpacing: "0",
  lineHeight: 1.16,
};

/* ─────────────────────────────────────────────────────────────────────────────
   PALETTE — dark photoreal ground, so the accent is AMBER (deep kumkum dies here).
   ───────────────────────────────────────────────────────────────────────────── */
export const C = {
  amber: "#F2A03C",
  amberDim: "#B4762C",
  cream: "#FFF4E2",
  creamDim: "rgba(255,244,226,0.62)",
  ink: "#100E0A",
  ember: "#E4622C",
} as const;

/** Type never sits raw on photographic detail — it carries its own dark bloom. */
export const halo = {
  textShadow:
    "0 2px 22px rgba(10,7,3,0.95), 0 0 44px rgba(10,7,3,0.75), 0 1px 2px rgba(10,7,3,0.9)",
};

/* ─────────────────────────────────────────────────────────────────────────────
   TIME
   ───────────────────────────────────────────────────────────────────────────── */

/** LAW 1 — animate on twos. Every hook below reads THIS, never useCurrentFrame. */
export const useStep = () => Math.floor(useCurrentFrame() / 2) * 2;

/** current time in seconds, quantised on twos */
export const useT = () => {
  const { fps } = useVideoConfig();
  return useStep() / fps;
};

export const SWING = { damping: 8, stiffness: 95, mass: 1.15 } as const;
export const STAMP = { damping: 15, stiffness: 260, mass: 0.7 } as const;
export const SETTLE = { damping: 200, stiffness: 75, mass: 0.5 } as const;
export const RISE = { damping: 11, stiffness: 120, mass: 1.0 } as const;

/** spring progress that starts at second `at` */
export const useSpringAt = (
  at: number,
  config: { damping: number; stiffness: number; mass: number } = SWING
) => {
  const frame = useStep();
  const { fps } = useVideoConfig();
  return spring({ frame: frame - Math.round(at * fps), fps, config });
};

/**
 * LAW 3 — one instance, many poses. Keyframe any scalar over absolute seconds.
 * Eased in-out cubic between stops, so a value that holds equal on both sides of a
 * pair of stops is genuinely STATIC (this is how the ground stays still).
 */
export const useKeys = (keys: readonly (readonly [number, number])[]) => {
  const t = useT();
  if (keys.length === 0) return 0;
  if (t <= keys[0][0]) return keys[0][1];
  const last = keys[keys.length - 1];
  if (t >= last[0]) return last[1];
  for (let i = 0; i < keys.length - 1; i++) {
    const [t0, v0] = keys[i];
    const [t1, v1] = keys[i + 1];
    if (t >= t0 && t < t1) {
      if (t1 === t0) return v1;
      return interpolate(t, [t0, t1], [v0, v1], {
        easing: Easing.inOut(Easing.cubic),
        extrapolateLeft: "clamp",
        extrapolateRight: "clamp",
      });
    }
  }
  return last[1];
};

/** Same, but linear — for anything that should not ease (progress rails, reveals). */
export const useKeysLinear = (keys: readonly (readonly [number, number])[]) => {
  const t = useT();
  if (keys.length === 0) return 0;
  if (t <= keys[0][0]) return keys[0][1];
  const last = keys[keys.length - 1];
  if (t >= last[0]) return last[1];
  for (let i = 0; i < keys.length - 1; i++) {
    const [t0, v0] = keys[i];
    const [t1, v1] = keys[i + 1];
    if (t >= t0 && t < t1)
      return t1 === t0 ? v1 : interpolate(t, [t0, t1], [v0, v1]);
  }
  return last[1];
};

/**
 * LAW 2 — swing in on an arc from a pivot ABOVE, overshoot, ring, idle on a slow
 * pendulum, then leave. Returns everything a caller needs to build a transform.
 * `idle` is opt-in: only the ONE bold object of a beat should breathe (motion budget).
 */
export const useSwing = (
  at: number,
  out?: number,
  opts: { from?: number; drop?: number; idle?: number } = {}
) => {
  const { from = -13, drop = 58, idle = 0 } = opts;
  const step = useStep();
  const { fps } = useVideoConfig();
  const sIn = useSpringAt(at, SWING);
  const sOut = useSpringAt(out ?? 1e6, SETTLE);
  const since = step - Math.round(at * fps);
  const breathe = idle > 0 && sIn > 0.85 ? Math.sin(since / 33) * idle : 0;
  const rot = interpolate(sIn, [0, 1], [from, 0]) + breathe;
  const dy = interpolate(sIn, [0, 1], [-drop, 0]);
  return {
    sIn,
    sOut,
    rot: rot + sOut * (from * 0.7),
    dy: dy + sOut * drop * 1.5,
    opacity: Math.min(sIn * 1.35, 1) * (1 - sOut),
    scale: interpolate(sIn, [0, 1], [0.9, 1]),
  };
};

/* ─────────────────────────────────────────────────────────────────────────────
   GROUND — the depth stack.
   ───────────────────────────────────────────────────────────────────────────── */

export type Slice = { src: string; z: number; blur: number; haze: number };

/** 5 inpainted slices per plate, farthest first. Built by depth_slice.py. */
export const sliceStack = (scene: string): Slice[] => [
  { src: `sanatan45/A/layers/${scene}_L0.png`, z: 1500, blur: 3.4, haze: 0.34 },
  { src: `sanatan45/A/layers/${scene}_L1.png`, z: 1017, blur: 2.1, haze: 0.22 },
  { src: `sanatan45/A/layers/${scene}_L2.png`, z: 588, blur: 1.15, haze: 0.12 },
  { src: `sanatan45/A/layers/${scene}_L3.png`, z: 231, blur: 0.4, haze: 0.045 },
  { src: `sanatan45/A/layers/${scene}_L4.png`, z: 0, blur: 0, haze: 0 },
];

const OVERSCAN = 1.2;

/**
 * One depth slice. `push` is the camera dolly (0 = rest, 1 = one full transition
 * worth of travel); nearer slices scale and drift MORE, which is the parallax.
 */
const SliceImg: React.FC<{ s: Slice; push: number; pan: number; dim: number }> = ({
  s,
  push,
  pan,
  dim,
}) => {
  const par = 1 - s.z / 1500; // 1 = nearest, 0 = farthest
  const scale = 1 + push * (0.055 + 0.135 * par);
  const tx = pan * (0.3 + 0.7 * par) * 96;
  return (
    <AbsoluteFill style={{ transform: `translateX(${tx}px) scale(${scale})` }}>
      <Img
        src={staticFile(s.src)}
        style={{
          position: "absolute",
          left: `${(1 - OVERSCAN) * 50}%`,
          top: `${(1 - OVERSCAN) * 50}%`,
          width: `${OVERSCAN * 100}%`,
          height: `${OVERSCAN * 100}%`,
          objectFit: "cover",
          filter: s.blur > 0 ? `blur(${s.blur}px)` : undefined,
        }}
      />
      {/* atmospheric perspective — distance is warm haze, not grey */}
      {s.haze > 0 && (
        <AbsoluteFill
          style={{
            background: "linear-gradient(180deg,#3A2A16 0%,#241A0E 100%)",
            opacity: s.haze * (1 - dim * 0.4),
            mixBlendMode: "screen",
          }}
        />
      )}
    </AbsoluteFill>
  );
};

/**
 * <Scene> — the full-bleed ground for one beat, with the cast rendered INSIDE it.
 *
 * ⭐ children land between slice `insertAfter` and the rest, so the near ground draws
 *   OVER the figures. This is the single reason the frame reads as a room and not a
 *   slide. Give the beat's hero a lower insertAfter to bring it forward.
 *
 * ⛔ LAW 6 (round-10 rule 6): push/pan MUST be constant while a beat is resident.
 *   Only the transition moves the ground.
 */
export const Scene: React.FC<{
  scene: string;
  push?: number;
  pan?: number;
  dim?: number;
  insertAfter?: number;
  children?: React.ReactNode;
}> = ({ scene, push = 0, pan = 0, dim = 0, insertAfter = 2, children }) => {
  const slices = sliceStack(scene);
  return (
    <AbsoluteFill style={{ backgroundColor: C.ink }}>
      {slices.slice(0, insertAfter + 1).map((s, i) => (
        <SliceImg key={`b${i}`} s={s} push={push} pan={pan} dim={dim} />
      ))}
      {/* the cast stands here — behind the near ground */}
      <AbsoluteFill>{children}</AbsoluteFill>
      {slices.slice(insertAfter + 1).map((s, i) => (
        <SliceImg key={`f${i}`} s={s} push={push} pan={pan} dim={dim} />
      ))}
      {dim > 0 && (
        <AbsoluteFill style={{ background: "#0B0906", opacity: dim }} />
      )}
    </AbsoluteFill>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   CAST — flat illustration standing in the photograph.
   ───────────────────────────────────────────────────────────────────────────── */

/** real pixel sizes, read off the PNGs — so aspect is never guessed */
export const DIM: Record<string, [number, number]> = {
  bi_cradle: [998, 893],
  rt_razor: [607, 604],
  rt_mandap: [688, 718],
  rt_threshold: [674, 548],
  rt_havan: [766, 738],
  rt_pot: [610, 555],
  pp_pandit: [720, 892],
  pp_phoneman: [440, 1173],
  pp_fatherson: [728, 1139],
  pp_elder: [720, 892],
  modern_man: [735, 1496],
  couple: [1286, 1024],
  dna: [374, 1443],
  rishi: [846, 1287],
  ic_scroll: [719, 632],
  ic_nameplate: [637, 566],
  ic_knot: [623, 226],
  vd_sage_seated: [842, 928],
  manuscript: [1300, 805],
  sc_ychrom: [504, 805],
};

/**
 * A cutout, anchored CENTRE-BOTTOM (things stand on a floor), sized by height only —
 * width follows the real aspect so nothing is ever squashed.
 * The shadow is what welds it to the plate (LAW 5 fix #2).
 */
export const Cast: React.FC<{
  src: string;
  x: number;
  y: number;
  h: number;
  rot?: number;
  opacity?: number;
  scale?: number;
  flip?: boolean;
  glow?: number;
  grade?: string;
  audit?: string;
}> = ({
  src,
  x,
  y,
  h,
  rot = 0,
  opacity = 1,
  scale = 1,
  flip = false,
  glow = 0,
  grade,
  audit,
}) => {
  const [dw, dh] = DIM[src] ?? [1000, 1000];
  const w = (dw / dh) * h;
  if (opacity <= 0.004) return null;
  return (
    <div
      data-audit={audit ?? `cast:${src}`}
      style={{
        position: "absolute",
        left: x - (w * scale) / 2,
        top: y - h * scale,
        width: w * scale,
        height: h * scale,
        opacity,
        transform: `rotate(${rot}deg)${flip ? " scaleX(-1)" : ""}`,
        transformOrigin: "50% -26%", // pivot ABOVE — it swings like a pendulum
      }}
    >
      <Img
        src={staticFile(`sanatan/cut/${src}.png`)}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "contain",
          filter: [
            "drop-shadow(0 22px 26px rgba(12,8,3,0.62))",
            "drop-shadow(0 3px 5px rgba(12,8,3,0.5))",
            glow > 0
              ? `drop-shadow(0 0 ${18 * glow}px rgba(242,160,60,${0.5 * glow}))`
              : "",
            grade ?? "",
          ]
            .filter(Boolean)
            .join(" "),
        }}
      />
    </div>
  );
};

/** photoreal sticker — evidence. Stiffer, no pendulum, cream keyline behind. */
export const Proof: React.FC<{
  src: string;
  x: number;
  y: number;
  h: number;
  rot?: number;
  opacity?: number;
  audit?: string;
}> = ({ src, x, y, h, rot = 0, opacity = 1, audit }) => {
  if (opacity <= 0.004) return null;
  return (
    <div
      data-audit={audit ?? `proof:${src}`}
      style={{
        position: "absolute",
        left: x,
        top: y,
        opacity,
        transform: `translate(-50%,-50%) rotate(${rot}deg)`,
      }}
    >
      <Img
        src={staticFile(`sanatan/photo/${src}.png`)}
        style={{
          height: h,
          filter:
            "drop-shadow(0 18px 22px rgba(10,7,2,0.7)) drop-shadow(0 0 2px rgba(255,244,226,0.85))",
        }}
      />
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   TYPE — every word lands on the frame it is spoken (LAW 6).
   ⛔ Devanagari is NEVER absolutely positioned; it always lives in flow layout,
      because its rendered width is unknowable at author time.
   ───────────────────────────────────────────────────────────────────────────── */

export type W = readonly [number, number, string];

/** one word, dropping in on its own spoken frame */
const Wd: React.FC<{
  at: number;
  text: string;
  accent?: boolean;
  face?: string;
  weight?: number;
  size: number;
  out?: number;
}> = ({ at, text, accent, face, weight = 800, size, out }) => {
  const s = useSpringAt(at, { damping: 12, stiffness: 175, mass: 0.85 });
  const o = useSpringAt(out ?? 1e6, SETTLE);
  return (
    <span
      style={{
        ...devaBlock,
        fontFamily: face ?? anek,
        fontWeight: weight,
        fontSize: size,
        color: accent ? C.amber : C.cream,
        ...halo,
        display: "inline-block",
        opacity: Math.min(s * 1.4, 1) * (1 - o),
        transform: `translateY(${interpolate(s, [0, 1], [size * 0.42, 0]) + o * -size * 0.3}px)`,
        marginRight: size * 0.19,
      }}
    >
      {text}
    </span>
  );
};

/**
 * A line of word-anchored type. `words` are [start, end, text] straight out of the
 * forced alignment; `accentIdx` marks the ONE word that is allowed to be amber.
 */
export const Line: React.FC<{
  words: W[];
  size: number;
  accentIdx?: number[];
  face?: string;
  weight?: number;
  out?: number;
  align?: "left" | "center" | "right";
  maxWidth?: number;
}> = ({
  words,
  size,
  accentIdx = [],
  face,
  weight = 800,
  out,
  align = "left",
  maxWidth,
}) => (
  <div
    style={{
      display: "flex",
      flexWrap: "wrap",
      alignItems: "baseline",
      justifyContent:
        align === "center" ? "center" : align === "right" ? "flex-end" : "flex-start",
      maxWidth,
      rowGap: size * 0.12,
    }}
  >
    {words.map((w, i) => (
      <Wd
        key={i}
        at={w[0]}
        text={w[2]}
        size={size}
        face={face}
        weight={weight}
        out={out}
        accent={accentIdx.includes(i)}
      />
    ))}
  </div>
);

/** small kicker above a headline — the quiet voice */
export const Kicker: React.FC<{
  at: number;
  out?: number;
  text: string;
  face?: string;
  color?: string;
  size?: number;
}> = ({ at, out, text, face, color = C.amber, size = 27 }) => {
  const s = useSpringAt(at, RISE);
  const o = useSpringAt(out ?? 1e6, SETTLE);
  return (
    <div
      style={{
        ...devaBlock,
        fontFamily: face ?? anek,
        fontWeight: 600,
        fontSize: size,
        color,
        ...halo,
        opacity: Math.min(s * 1.5, 1) * (1 - o),
        transform: `translateY(${interpolate(s, [0, 1], [14, 0])}px)`,
        display: "flex",
        alignItems: "center",
        gap: 12,
      }}
    >
      <span
        style={{
          display: "inline-block",
          width: interpolate(s, [0, 1], [0, 34]),
          height: 2,
          background: color,
          opacity: 0.9,
        }}
      />
      {text}
    </div>
  );
};

/**
 * A label welded to an object. Lives in flow inside its own centred box, so the
 * Devanagari can be any width it likes and will never collide with a neighbour.
 */
export const Tag: React.FC<{
  at: number;
  out?: number;
  text: string;
  x: number;
  y: number;
  size?: number;
  color?: string;
  weight?: number;
  face?: string;
  audit?: string;
}> = ({
  at,
  out,
  text,
  x,
  y,
  size = 30,
  color = C.cream,
  weight = 700,
  face,
  audit,
}) => {
  const s = useSpringAt(at, { damping: 13, stiffness: 200, mass: 0.8 });
  const o = useSpringAt(out ?? 1e6, SETTLE);
  const op = Math.min(s * 1.5, 1) * (1 - o);
  if (op <= 0.004) return null;
  return (
    <div
      data-audit={audit ?? `tag:${text}`}
      style={{
        position: "absolute",
        left: x,
        top: y,
        transform: `translate(-50%,0) translateY(${interpolate(s, [0, 1], [11, 0])}px)`,
        display: "flex",
        justifyContent: "center",
        opacity: op,
      }}
    >
      <span
        style={{
          ...devaBlock,
          fontFamily: face ?? anek,
          fontWeight: weight,
          fontSize: size,
          color,
          whiteSpace: "nowrap",
          ...halo,
        }}
      >
        {text}
      </span>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   THE THREAD — one object, five meanings, present for all 45.6 seconds.
   phone cord → corridor line → root → sutra → lineage.  (LAW 3, at film scale.)
   ───────────────────────────────────────────────────────────────────────────── */

export type Curve = readonly [number, number, number, number, number, number, number, number];

/** lerp a whole 8-vector of bezier control points across time — ONE hook, any shape */
export const useCurve = (keys: readonly (readonly [number, Curve])[]): Curve => {
  const t = useT();
  if (t <= keys[0][0]) return keys[0][1];
  const last = keys[keys.length - 1];
  if (t >= last[0]) return last[1];
  for (let i = 0; i < keys.length - 1; i++) {
    const [t0, c0] = keys[i];
    const [t1, c1] = keys[i + 1];
    if (t >= t0 && t < t1) {
      const k = interpolate(t, [t0, t1], [0, 1], {
        easing: Easing.inOut(Easing.cubic),
      });
      return c0.map((v, j) => v + (c1[j] - v) * k) as unknown as Curve;
    }
  }
  return last[1];
};

export const Thread: React.FC<{
  c: Curve;
  reveal?: number;
  width?: number;
  opacity?: number;
  color?: string;
  glow?: boolean;
}> = ({ c, reveal = 1, width = 3, opacity = 1, color = C.amber, glow = true }) => {
  const d = `M ${c[0]} ${c[1]} C ${c[2]} ${c[3]}, ${c[4]} ${c[5]}, ${c[6]} ${c[7]}`;
  return (
    <svg
      width={1280}
      height={720}
      viewBox="0 0 1280 720"
      style={{ position: "absolute", left: 0, top: 0, overflow: "visible" }}
    >
      {glow && (
        <path
          d={d}
          fill="none"
          stroke={color}
          strokeWidth={width * 4.2}
          strokeLinecap="round"
          opacity={0.16 * opacity}
          style={{ filter: "blur(7px)" }}
          pathLength={1}
          strokeDasharray={1}
          strokeDashoffset={1 - reveal}
        />
      )}
      <path
        d={d}
        fill="none"
        stroke={color}
        strokeWidth={width}
        strokeLinecap="round"
        opacity={opacity}
        pathLength={1}
        strokeDasharray={1}
        strokeDashoffset={1 - reveal}
      />
    </svg>
  );
};

/** a bead riding the thread — the same dot that becomes a generation node */
export const beadAt = (c: Curve, u: number): [number, number] => {
  const m = 1 - u;
  const x =
    m * m * m * c[0] + 3 * m * m * u * c[2] + 3 * m * u * u * c[4] + u * u * u * c[6];
  const y =
    m * m * m * c[1] + 3 * m * m * u * c[3] + 3 * m * u * u * c[5] + u * u * u * c[7];
  return [x, y];
};

export const Bead: React.FC<{ x: number; y: number; r?: number; o?: number }> = ({
  x,
  y,
  r = 7,
  o = 1,
}) => (
  <div
    style={{
      position: "absolute",
      left: x - r,
      top: y - r,
      width: r * 2,
      height: r * 2,
      borderRadius: "50%",
      background: C.amber,
      opacity: o,
      boxShadow: `0 0 ${r * 2.6}px rgba(242,160,60,0.85), 0 0 ${r * 6}px rgba(242,160,60,0.4)`,
    }}
  />
);

/* ─────────────────────────────────────────────────────────────────────────────
   ATMOSPHERE — grade, grain, vignette. This is what stops it reading as a slide.
   ⛔ LAW 8: these are LIGHT, not shapes. No rings, no dots, no mandalas.
   ───────────────────────────────────────────────────────────────────────────── */

const GRAIN =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="180" height="180"><filter id="n"><feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="3" stitchTiles="stitch"/><feColorMatrix type="saturate" values="0"/></filter><rect width="180" height="180" filter="url(%23n)" opacity="0.5"/></svg>`
  );

export const Atmosphere: React.FC<{ warm?: number }> = ({ warm = 1 }) => (
  <>
    {/* one warm key from upper-left, so every plate is lit by the same lamp */}
    <AbsoluteFill
      style={{
        background:
          "radial-gradient(120% 90% at 22% 8%, rgba(255,196,110,0.20) 0%, rgba(255,150,60,0.07) 34%, rgba(0,0,0,0) 68%)",
        opacity: warm,
        mixBlendMode: "screen",
        pointerEvents: "none",
      }}
    />
    {/* vignette — pulls the eye off the corners without drawing anything */}
    <AbsoluteFill
      style={{
        background:
          "radial-gradient(128% 100% at 50% 46%, rgba(0,0,0,0) 42%, rgba(0,0,0,0.42) 78%, rgba(0,0,0,0.72) 100%)",
        pointerEvents: "none",
      }}
    />
    {/* grain — the difference between "a photo" and "a film" */}
    <AbsoluteFill
      style={{
        backgroundImage: `url("${GRAIN}")`,
        backgroundSize: "180px 180px",
        opacity: 0.09,
        mixBlendMode: "overlay",
        pointerEvents: "none",
      }}
    />
  </>
);

/** the quiet frame furniture: wordmark + a hairline that measures the 45 seconds */
export const Rail: React.FC<{ total: number }> = ({ total }) => {
  const t = useT();
  const p = Math.max(0, Math.min(1, t / total));
  return (
    <>
      <div
        style={{
          position: "absolute",
          left: 46,
          bottom: 34,
          ...devaBlock,
          fontFamily: anek,
          fontWeight: 600,
          fontSize: 16,
          color: "rgba(255,244,226,0.5)",
          textShadow: "0 1px 8px rgba(0,0,0,0.9)",
        }}
      >
        सनातन डिकोड
      </div>
      <div
        style={{
          position: "absolute",
          left: 46,
          right: 46,
          bottom: 22,
          height: 1,
          background: "rgba(255,244,226,0.14)",
        }}
      />
      <div
        style={{
          position: "absolute",
          left: 46,
          bottom: 22,
          width: (1280 - 92) * p,
          height: 1,
          background: C.amber,
          opacity: 0.72,
        }}
      />
    </>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   BEAT TABLE — asserted arithmetically, because two beats sharing seven seconds
   is what produced the 0:58 pile-up on v17/v18.
   ───────────────────────────────────────────────────────────────────────────── */
export type BeatDef = { id: string; a: number; b: number; scene: string };

export const assertNoOverlap = (beats: BeatDef[]) => {
  for (let i = 0; i < beats.length - 1; i++) {
    if (beats[i].b > beats[i + 1].a) {
      throw new Error(
        `BEAT OVERLAP: ${beats[i].id} ends ${beats[i].b} but ${beats[i + 1].id} starts ${beats[i + 1].a}`
      );
    }
  }
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
